<template>
  <section class="sessionContainer">
    <div>
      <section class="card">
        <span>
          <span class="label">Type:</span>
          <span class="value">{{
            updatedSession.template ? updatedSession.template.model : ""
          }}</span>
        </span>
        <span>
          <span class="label">SW:</span>
          <span class="value">{{
            updatedSession.template ? updatedSession.template.edition : ""
          }}</span>
        </span>
        <span>
          <span class="label">Status:</span>
          <span data-test="machine-state" class="value">{{
            updatedSession.machineState
          }}</span>
        </span>
      </section>
      <section class="card">
        <span>
          <span class="label">Shutdown:</span>
          <span
            class="value"
            :class="highlightShutdownTime ? 'highlight' : ''"
            data-test="shutdown-time"
            >{{ isSessionActive ? shutdownTime : "N/A" }}</span
          >
        </span>
        <span>
          <span class="label">Credits/h:</span>
          <span class="value">{{
            updatedSession.template ? updatedSession.template.price : ""
          }}</span>
        </span>
        <span>
          <span class="label">Running since:</span>
          <span class="value">{{ isSessionActive ? startTime : "N/A" }}</span>
        </span>
      </section>
    </div>
    <div>
      <section class="sessionLabelContainer">
        <span>Label:</span>
        <input
          :disabled="!canEditLabelData"
          class="input"
          type="text"
          data-test="session-label"
          v-model="sessionLabel"
        />
        <font-awesome-icon
          v-show="canEditLabelData"
          @click="handleIconClick"
          icon="save"
          data-test="label-button"
          role="button"
          class="icon"
        />
      </section>
    </div>

    <div class="connection-details-container">
      <section>
        <div class="connection-details">
          <div>
            <a-tooltip placement="rightTop" :mouseEnterDelay="0.5">
              <template slot="title">
                View the connection details. The connection details show the DNS
                name or IP address of the virtual machine. In addition,
                passwords for the machine and any VPN connection are displayed.
              </template>
              <Button
                @onClick="handleShowModal"
                :disabled="!isSessionActive || !isAuthorized"
                class="defaultButton"
                >Connection
                <font-awesome-icon icon="plug" class="open-icon" />
              </Button>
            </a-tooltip>
            <section>
              <a-tooltip
                placement="rightTop"
                :mouseEnterDelay="0.5"
                v-if="!isVNCOpening"
              >
                <template slot="title">
                  Open a VNC direct connection in the browser window. The VNC
                  connection represents another type of remote desktop
                  connection. VNC is currently available only in case of VPN
                  connection.
                </template>
                <Button
                  :disabled="
                    !isSessionActive ||
                      !updatedSession.useVPNSetup ||
                      !isAuthorized
                  "
                  @onClick="handleOpenClick"
                  class="defaultButton"
                >
                  {{ newWindowLabel }}
                  <font-awesome-icon
                    icon="external-link-alt"
                    class="open-icon"
                  />
                </Button>
              </a-tooltip>

              <section v-else class="vnc-section-loader">
                <a-icon
                  slot="indicator"
                  type="loading"
                  style="font-size: 24px"
                  spin
                />
              </section>
            </section>
            <section>
              <a-tooltip
                placement="rightTop"
                :mouseEnterDelay="0.5"
                v-if="!isRDPDownloading"
              >
                <template slot="title">
                  Downloads a RDP connection file that can be used to establish
                  a remote desktop connection. This button also registers the IP
                  address in the virtual machine firewall. When clicked, the
                  Windows password is provided to the clipboard.
                </template>

                <Button
                  :disabled="!isSessionActive || !isAuthorized"
                  class="defaultButton"
                  @onClick.stop="triggerRDPDownload"
                >
                  RDP
                  <font-awesome-icon icon="desktop" class="open-icon" />
                </Button>
              </a-tooltip>
              <section v-else class="vnc-section-loader">
                <a-icon
                  slot="indicator"
                  type="loading"
                  style="font-size: 24px"
                  spin
                />
              </section>
            </section>
          </div>
          <div class="config-box">
            <div class="config-pair">
              <span class="label">Auto shutdown (hours)</span>
              <div class="timeout-section config-input">
                <a-input-number
                  id="inputNumber"
                  @change="handleDelayedShutdownTimeSelection"
                  v-model="selectedShutdownTime"
                  :min="0"
                  :max="24"
                  :parser="parseTimeout"
                  :formatter="formatTimeout"
                  :disabled="!isAuthorized"
                />
              </div>
            </div>
            <div class="config-pair">
              <span class="label">Use VPN tunnel</span>
              <a-checkbox
                class="vpn-checkbox config-input"
                :checked="useVPNSetup"
                :disabled="isSessionActive || !isAuthorized"
                @change="onVPNSetupChange"
              />
            </div>
          </div>
          <div class="action-section">
            <Button
              :disabled="isStartStopButtonDisabled"
              class="stopButton"
              @onClick="handleStartStopClick"
              >{{ isSessionActive ? "Stop" : "Start" }}</Button
            >
          </div>
        </div>
      </section>
    </div>

    <popup
      v-if="isAuthorized"
      v-show="showModal && updatedSession"
      @close="showModal = false"
      :useVPNSetup="updatedSession.useVPNSetup"
      :public-d-n-s="updatedSession.publicDNS"
      :publicIP="updatedSession.publicIP"
      :computerName="
        updatedSession.connectionDetails
          ? updatedSession.connectionDetails.computerName
          : ''
      "
      :vpn-password="
        updatedSession.connectionDetails &&
          updatedSession.connectionDetails.vpnPassword
      "
      :vpn-user="
        updatedSession.connectionDetails &&
          updatedSession.connectionDetails.vpnUser
      "
      :win-admin-user="
        updatedSession.connectionDetails &&
          updatedSession.connectionDetails.winAdminUser
      "
      :win-admin-password="
        updatedSession.connectionDetails &&
          updatedSession.connectionDetails.winAdminPassword
      "
      :privateIP="
        updatedSession.connectionDetails &&
          updatedSession.connectionDetails.privateIP
      "
    >
      <h3 slot="header">
        Connection details<font-awesome-icon icon="plug" class="open-icon" />
      </h3>
    </popup>
  </section>
</template>

<script>
import { format } from "date-fns";
import Button from "@/components/Button.vue";
import Popup from "@/components/Popup";
import { TIME_FORMAT, ONE_MINUTE } from "@/config/time";

import { updateSessionDetails, getOneSession } from "@/api/vm";
import { getMachineState } from "@/utils/vm";

export default {
  name: "SessionDetails",
  components: { Button, Popup },
  props: {
    session: {
      type: Object,
      default: function() {
        return {};
      }
    },
    isVNCOpening: Boolean,
    newWindowLabel: {
      type: String,
      required: true
    }
  },
  data: function() {
    return {
      isRDPDownloading: false,
      canEditLabelData: this.$route.name === "Session",
      isLabelEditable: false,
      showModal: false,
      currentJobTimeoutId: null,
      useVPNSetup: this.session.useVPNSetup,
      sessionLabel: this.session.label,
      updatedSession: this.session,
      selectedShutdownTime: 0,
      selectShutdownTimeTimer: null,
      highlightShutdownTime: false
    };
  },
  mounted() {
    // Ant-Design-Numberinput doesn't accept computed as defaults
    this.selectedShutdownTime = this.timeoutHours;
  },
  beforeDestroy() {
    if (this.currentJobTimeoutId) {
      clearInterval(this.currentJobTimeoutId);
    }
    this.$store.dispatch("pollCredits", ONE_MINUTE);
    this.$store.dispatch("pollSessions", ONE_MINUTE);
  },
  watch: {
    session: {
      handler: function(val) {
        this.updatedSession = val;
      },
      deep: true
    }
  },
  computed: {
    isStartStopButtonDisabled() {
      if (
        this.machineState === "STOPPING" ||
        this.machineState === "STARTING"
      ) {
        return true;
      } else if (this.machineState === "STOPPED") {
        return !this.isAuthorized;
      }
      return false;
    },
    isAuthorized() {
      return this.updatedSession.authorized;
    },
    isSessionActive: function() {
      if (this.updatedSession) {
        let state = getMachineState(this.updatedSession.machineStateValue);
        return state === "STARTING" || state === "RUNNING";
      }
      return false;
    },
    machineState() {
      return getMachineState(this.updatedSession.machineStateValue);
    },
    shutdownTime() {
      if (
        !this.updatedSession ||
        (this.updatedSession && !this.updatedSession.expectedShutdownTime)
      ) {
        return "Disabled";
      }
      return format(
        new Date(this.updatedSession.expectedShutdownTime || ""),
        TIME_FORMAT
      );
    },
    startTime() {
      if (
        !this.updatedSession ||
        (this.updatedSession && !this.updatedSession.startTime)
      ) {
        return "N/A";
      }
      return format(new Date(this.updatedSession.startTime || ""), TIME_FORMAT);
    },
    timeoutHours() {
      let timeout = 0;
      if (this.updatedSession) {
        timeout = Number(this.updatedSession.timeoutHours || 0);
      }
      return timeout;
    }
  },
  methods: {
    parseTimeout(string) {
      string = string.replace(/\sh/, "");
      if (isNaN(string)) {
        return 0;
      }
      return Number.parseInt(string);
    },
    formatTimeout(value) {
      if (isNaN(value) | (Number.parseInt(value) === 0)) {
        return "disabled";
      }
      return Number.parseInt(value) + " h";
    },
    onVPNSetupChange(e) {
      this.useVPNSetup = Boolean(e.target.checked);
    },
    handleIconClick() {
      this.updateSessionDetail();
    },
    handleShowModal() {
      if (this.updatedSession && this.updatedSession.connectionDetails) {
        this.showModal = true;
      }
    },
    handleOpenClick(e) {
      this.$emit("newWindowClick", e, this.updatedSession.id);
    },
    async updateSessionDetail() {
      try {
        await updateSessionDetails(this.updatedSession.id, {
          label: this.sessionLabel,
          timeoutHours: this.updatedSession.timeoutHours
        });
        await this.getSingleSession();
      } catch (error) {
        this.$notification.error({
          message: error.message || "Could not update session",
          description: "Unable to update session"
        });
      }
    },
    handleDelayedShutdownTimeSelection() {
      if (this.selectedShutdownTimeTimer !== null) {
        window.clearTimeout(this.selectedShutdownTimeTimer);
      }
      this.selectedShutdownTimeTimer = setTimeout(() => {
        this.handleShutdownMenuClick(this.selectedShutdownTime);
      }, 1000);
    },
    async handleShutdownMenuClick(val = 0) {
      try {
        await updateSessionDetails(this.updatedSession.id, {
          label: this.updatedSession.label,
          timeoutHours: val || 0
        });
        await this.getSingleSession();
        this.highlightShutdownTime = true;
        setTimeout(() => {
          this.highlightShutdownTime = false;
        }, 1000);
      } catch (error) {
        this.$notification.error({
          message: error.message || "Could not update session",
          description: "Unable to update session"
        });
      }
    },
    async getSingleSession() {
      try {
        const response = await getOneSession(this.updatedSession.id);
        this.updatedSession = response.data;
      } catch (error) {
        this.$emit("refetchSessions");
      }
    },
    async handleStartStopClick() {
      if (this.isSessionActive) {
        console.log("stopping VM session");
        await this.handleStop();
      } else {
        console.log("starting VM session");
        await this.handleRestart();
      }
    },
    async handleRestart() {
      let data = {
        sessionId: this.updatedSession.id,
        timeoutHours: this.updatedSession.timeoutHours,
        useVPNSetup: this.useVPNSetup
      };
      this.$emit("restartSession", data);
      this.updatedSession.machineStateValue = 1;
    },
    async handleStop() {
      this.$emit("stopSession", this.updatedSession.id);
      this.updatedSession.machineStateValue = 3;
    },
    triggerRDPDownload() {
      this.isRDPDownloading = true;
      this.$emit("downloadRDP", this.updatedSession.id);
      // disable the button to download RDP for a moment
      setTimeout(() => {
        this.isRDPDownloading = false;
      }, 1000);
    }
  }
};
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
//used for the Popup as well
.open-icon {
  color: $accentColor;
  margin-left: $m2;
}
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "@/styles/_variables.scss";
.menu-container {
  max-width: 373px;
  &:hover {
    background: $white im !important;
  }
}
.menu-item-container {
  display: flex !important;
  flex-wrap: wrap;
  flex-direction: row;

  &:hover {
    background: $white !important;
  }

  & .button {
    width: 100px;
    height: 30px;
    background: $defaultColor;
    color: $textColor;
    box-shadow: none;
    font-size: $textSmall;
    margin: $m1;

    &:focus,
    &:hover {
      background: $defaultFocus;
    }
  }
}

.action-section {
  align-items: flex-end;
}

.vnc-section-loader {
  align-self: center;
  width: 150px;
  margin-left: 8px;
  margin-right: 8px;
}

.connection-details-container {
  display: flex;
  padding: $p2;
  justify-content: center;

  & > section {
    display: inherit;
    align-items: flex-end;
  }
}

.connection-details {
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin-right: $m1;

  & > * {
    display: flex;
    justify-content: center;
  }

  & > *:first-child {
    margin-bottom: $m2;
  }

  & > .config-box {
    margin-bottom: $m2;
    display: flex;
    align-items: center;
    flex-direction: column;

    & > .config-pair {
      height: 45px;
      width: 80%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      & > .config-input {
        width: 30%;
      }
      & > .label {
        width: 70%;
      }
    }
  }
}

.highlight {
  font-weight: bold;
}

.vpn-checkbox {
  margin-bottom: 4px;
}
.sessionContainer {
  display: flex;
  flex-direction: column;

  .processingContainer {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 80%;
    align-self: center;
    margin-bottom: $m2;
  }

  .defaultButton {
    width: 150px;
    height: 40px;
    font-size: $baseTextSize;
    font-size: 200;
    padding: 4px;
    margin-right: $m1;
    margin-left: $m1;
    color: $textColor;
    background: $defaultColor;
    box-shadow: none;

    & .open-icon {
      color: $accentColor;
      margin-left: $m2;
    }

    &:focus,
    &:hover {
      background: $defaultColor;
    }
    &:disabled {
      background: gray;
      opacity: 30%;
    }
  }

  .stopButton {
    width: 150px;
    height: 40px;
    font-size: $baseTextSize;
    margin-right: $m1;
    margin-left: $m1;
  }

  .dropdown-button {
    width: 150px;
    height: 40px;
    background: #556679;
    font-size: $baseTextSize;
    margin-right: $m1;
    margin-left: $m1;
    box-shadow: none;
    border-radius: 20px;

    & .icon {
      margin-right: $m2;
      color: $white;
    }

    &:focus,
    &:hover {
      background: #556679;
    }
  }

  .sessionLabelContainer {
    background-color: $white;
    border-radius: 8px;
    width: 320px;
    height: 40px;
    display: flex;
    justify-content: flex-start;
    align-items: center;

    & span {
      margin-left: $m4;
      font-size: $textSmall;
      font-weight: 700;
      color: $textColor;
    }

    & .input {
      border: none;
      outline: none;
      margin-left: $m1;
      color: $textColor;
      font-weight: 700;
      font-size: $textSmall;
      &:disabled {
        background: none;
      }
    }
  }

  & > div {
    display: flex;
    flex-direction: row;
    justify-content: center;
    padding: $p2;

    & .card {
      min-width: 244px;
      min-height: 149px;
      background-color: #ffffff;
      margin-right: $m2;
      margin-left: $m2;
      display: flex;
      flex-direction: column;
      padding: $p1;

      & span {
        padding-top: $p1;
        display: flex;
        justify-content: flex-start;
        text-align: left;

        & .label {
          font-size: $textSmall;
          color: $textColor;
          min-width: 102px;
        }

        & .value {
          font-size: $textSmall;
          color: $textColor;
        }
      }
    }
  }

  @media screen and (max-width: 496px) {
    & > div {
      flex-wrap: wrap;

      & .card {
        width: 100%;
        margin-bottom: $m2;
      }
    }
  }
}
</style>
